import React, { useEffect } from 'react';
import { Tabs } from '@hills-design-system/components';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import Adoptions from './components/Adoptions';
import Insights from './components/Insights';
import PetParentReports from './components/PetParentReports';

import { useSearchParams } from 'react-router-dom';
import { analytics } from '../../services/analytics';
import { useShelterState } from '../../hooks/useShelter';
import PetszelReportingBanner from '../../components/PetszelReporting/PetszelReportingBanner';
import { ComplianceWarningBanner } from '../../components/ComplianceWarningBanner/ComplianceWarningBanner';
import { useInsightsState, useInsightsActions } from '../../hooks/useInsights';
import { TimePeriod } from '../../types/insights';
import { isReportingPaused } from '../../utils/featureFlags';
import { ReportingPausedBanner } from '../../components/ReportingPaused/ReportingPausedBanner';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

const DEFAULT_TAB = 'pet-parents';

const Reports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = React.useState(tabParam ?? DEFAULT_TAB);
  const { t } = useTranslation();
  const { shelter } = useShelterState();
  const { insights } = useInsightsState();
  const { getDashboardInsights } = useInsightsActions();
  const { enablePetszelPromotion } = useFeatureFlags();

  // Fetch dashboard insights to check compliance status.
  // Skip when the Insights tab is active – that tab fetches insights itself
  // (with the user-selected period) and the response already contains the
  // compliance fields. Firing this in parallel races with the Insights call
  // and overwrites the chart data with a single-month payload.
  useEffect(() => {
    if (shelter?.is_petszel_enabled && activeTab !== 'insights') {
      getDashboardInsights({ period: TimePeriod.MONTH });
    }
  }, [shelter?.is_petszel_enabled, getDashboardInsights, activeTab]);

  useEffect(() => {
    // If the tab param changes externally, update the state
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    // If no tab param, set it to default
    if (!tabParam) {
      setSearchParams({ tab: DEFAULT_TAB });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  const tabItems = [
    { id: 'pet-parents', label: t(TranslationKeyEnum.PetParents) },
    { id: 'adoptions', label: t(TranslationKeyEnum.Adoptions) },
    { id: 'insights', label: t(TranslationKeyEnum.Insights) },
  ];

  const handleTabChange = (tabId: string) => {
    const tabName = tabItems.find((item) => item.id === tabId)?.label ?? '';
    analytics.navigate('', tabName, 'tabselect', null);

    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'pet-parents':
        return <PetParentReports />;
      case 'adoptions':
        return <Adoptions />;
      case 'insights':
        return <Insights />;
      default:
        return <PetParentReports />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <main className="flex-grow">
        {/* Page Header */}
        <div className=" pt-10 pb-12 flex gap-6 flex-col">
          <h1 className="text-heading-400 leading-heading font-bold tracking-[-3%] text-gray-925">
            {t(TranslationKeyEnum.ReportsTitle)}
          </h1>
          <p className="text-body-125 leading-body text-gray-925 font-medium">
            {t(TranslationKeyEnum.ReportsDescription)}
          </p>
        </div>

        {shelter?.is_petszel_enabled && isReportingPaused() && (
          <ReportingPausedBanner className="mb-8" />
        )}

        {shelter && !shelter.is_petszel_enabled && enablePetszelPromotion && <PetszelReportingBanner className="mb-12" />}

        {/* Compliance Warning Banner - Shows above tabs for all tabs */}
        {shelter?.is_petszel_enabled &&
          insights?.complianceWarning?.show &&
          !isReportingPaused() && (
            <div className="mb-8">
              <ComplianceWarningBanner />
            </div>
          )}

        {/* Tabs */}
        <div className="" role="tablist">
          <Tabs items={tabItems} activeTab={activeTab} onChange={handleTabChange} />
        </div>

        {/* Content */}
        <div className=" py-8 flex flex-col items-center">{renderTabContent()}</div>
      </main>
    </div>
  );
};

export default Reports;
